import { create } from 'zustand';

import type { FileDto } from '@/api/api.dto';
import {
  API,
  deleteRequest,
  getRequest,
  getRequestBlob,
  postRequest,
  putRequest,
} from '@/api/api.ts';
import type {
  AssignRolePayload,
  BidRoom,
  ChatRoom,
  CreateChatRoom,
  SearchResults,
  UpdateMessageReq,
} from '@/store/chats/chat.dto.ts';
import { downloadBlob } from '@/utils/download-file-from-link.ts';
import { invalidateAuthorizedFile } from '@/utils/file-links';

import {
  getAllRooms,
  getBidRooms,
  removeRoomUser,
  updateTemporaryRoomDeletionDate,
} from './chat.api.ts';

type ChatsState = {
  chatRooms: ChatRoom[] | null;
  chatRoom: ChatRoom | null;
  bids: BidRoom[] | null;
  bid: BidRoom | null;
  searchResults: SearchResults | null;
  isLoading: boolean;
  getChatRooms: (search?: string) => Promise<ChatRoom[]>;
  getCurrentRoomsChats: (roomId: string) => Promise<ChatRoom>;
  createChatRoom: (body: CreateChatRoom) => Promise<ChatRoom>;
  updateChatRoom: (id: string, body: CreateChatRoom) => Promise<void>;
  pinChatRoom: (id: string, pin: boolean) => Promise<void>;
  removeChatRoom: (id: string) => Promise<void>;
  assignChatRoom: (id: string, usersIds: string[]) => Promise<void>;
  removeUserFromChat: (roomId: string, userId: string) => Promise<void>;
  createOneToOneChatRoom: (userId: string, folderId?: string) => Promise<ChatRoom>;
  getOrCreateOneToOneRoom: (userId: string, folderId?: string) => Promise<ChatRoom>;
  createEmailChatRoom: (serviceProviderId: string, subject: string) => Promise<ChatRoom>;
  addRoomToFolders: (id: string, folderIds: string[]) => Promise<void>;
  removeRoomFromFolder: (id: string, folderId: string) => Promise<void>;
  muteChat: (id: string, isMute: { mute: boolean }) => Promise<void>;
  assignRole: (id: string, body: AssignRolePayload) => Promise<void>;
  markMessagesAsRead: (roomId: string, messageIds: string[]) => Promise<void>;
  markMessagesAsUnread: (roomId: string) => Promise<void>;
  markRoomAsUnreadLocally: (roomId: string) => void;
  markRoomAsReadLocally: (roomId: string) => void;
  editMessage: (roomId: string, body: UpdateMessageReq) => Promise<void>;
  deleteMessage: (roomId: string, messageId: string) => Promise<void>;
  exportChatInPdf: (roomId: string | undefined) => Promise<any>;
  uploadRoomAvatar: (roomId: string, imageFile: File) => Promise<FileDto>;
  removeRoomAvatar: (roomId: string) => Promise<void>;
  postBid: (
    serviceProviderId: string,
    topicCode: string,
    folder: string | null,
  ) => Promise<BidRoom>;
  getBids: (topicCode: string) => Promise<BidRoom[]>;
  updateTemporaryRoomDeletionDate: (
    roomId: string,
    galleryId: string,
    removeAfter: Date,
  ) => Promise<void>;
  searchRooms: (query: string) => Promise<SearchResults | null>;
  clearSearchResults: () => void;
  error: string | null;
  addUsersToRoom: (roomId: string, userIds: string[]) => Promise<void>;
  removeUsersFromRoom: (roomId: string, userId: string) => Promise<void>;
};

const toError = (err: unknown) => (err instanceof Error ? err.message : String(err));

const normalizeChat = (chat: ChatRoom): ChatRoom => ({
  ...chat,
  id: String(chat.id),
  creator_id: String(chat.creator_id),
  folder_ids: chat.folder_ids ?? [],
  users: (chat.users ?? []).map((user) => ({
    ...user,
    id: String(user.id),
  })),
  last_message: chat.last_message
    ? {
        ...chat.last_message,
        id: String(chat.last_message.id),
        creator_id: String(chat.last_message.creator_id),
        room_id: chat.last_message.room_id
          ? String(chat.last_message.room_id)
          : chat.last_message.room_id,
      }
    : null,
});

const mergeChats = (existing: ChatRoom, incoming: ChatRoom): ChatRoom => {
  const normalizedIncoming = normalizeChat(incoming);

  const mergedLastMessage =
    normalizedIncoming.last_message !== null
      ? normalizedIncoming.last_message
      : existing.last_message;

  const mergedUsers =
    normalizedIncoming.users && normalizedIncoming.users.length > 0
      ? normalizedIncoming.users
      : existing.users;

  return {
    ...existing,
    ...normalizedIncoming,
    name: normalizedIncoming.name ?? existing.name,
    description: normalizedIncoming.description ?? existing.description,
    // A room from the server always carries `image`, null once it is removed; only a partial
    // payload (pin, mute) leaves it out and keeps the one already here.
    image: normalizedIncoming.image !== undefined ? normalizedIncoming.image : existing.image,
    folder_ids: normalizedIncoming.folder_ids?.length
      ? normalizedIncoming.folder_ids
      : (existing.folder_ids ?? []),
    last_message: mergedLastMessage,
    users: mergedUsers,
  };
};

const upsertChat = (list: ChatRoom[] | null, chat: ChatRoom) => {
  const normalized = normalizeChat(chat);
  if (!list) return [normalized];

  const existsIndex = list.findIndex((room) => String(room.id) === normalized.id);
  if (existsIndex === -1) {
    return [normalized, ...list];
  }

  return list.map((room, idx) => (idx === existsIndex ? mergeChats(room, normalized) : room));
};

// Only the newest getChatRooms walk writes to the store: an older one, still stepping through a
// different search, would otherwise overwrite the list the newer one just set.
let roomsWalk = 0;

export const useChats = create<ChatsState>((set, get) => ({
  chatRooms: null,
  chatRoom: null,
  bids: null,
  bid: null,
  searchResults: null,
  isLoading: false,
  error: null,
  getChatRooms: async (search?: string) => {
    const walk = ++roomsWalk;
    const isLatest = () => walk === roomsWalk;
    set({ isLoading: true });
    try {
      const rooms = await getAllRooms({ search }, (partial) => {
        if (isLatest()) {
          set({ chatRooms: partial.map(normalizeChat), isLoading: false, error: null });
        }
      });
      const chatRooms = rooms.map((room) => normalizeChat(room));
      if (isLatest()) {
        set({ chatRooms, error: null });
      }
      return chatRooms;
    } catch (err) {
      if (isLatest()) {
        set({ error: toError(err) });
      }
    } finally {
      if (isLatest()) {
        set({ isLoading: false });
      }
    }
    return [];
  },
  searchRooms: async (query: string) => {
    if (!query.trim()) {
      set({ searchResults: null });
      return null;
    }
    set({ isLoading: true });
    try {
      const response = await getRequest<SearchResults>(
        API.CHAT,
        `/rooms/search?search=${encodeURIComponent(query)}`,
        false,
        { limit: 500 },
      );
      const results = {
        messages: response.data.messages ?? [],
        rooms: response.data.rooms ?? [],
      };
      set({ searchResults: results, error: null });
      return results;
    } catch (err) {
      set({ error: toError(err) });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  clearSearchResults: () => set({ searchResults: null }),
  getCurrentRoomsChats: async (roomId: string) => {
    try {
      const response = await getRequest<ChatRoom>(API.CHAT, `/rooms/${roomId}`);
      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        chatRoom: response.data,
        error: null,
      }));
      return response.data;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  createChatRoom: async (body: CreateChatRoom) => {
    try {
      const response = await postRequest<ChatRoom, CreateChatRoom>(API.CHAT, `/rooms`, body);

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        error: null,
      }));
      return response.data;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  createOneToOneChatRoom: async (userId: string, folderId?: string) => {
    try {
      const payload: Record<string, unknown> = { user: userId };
      if (folderId) {
        payload.folder_id = folderId;
      }

      const response = await postRequest<any, any>(API.CHAT, `/rooms/one-to-one`, payload);

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        error: null,
      }));
      return response.data;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  /**
   * The room you and this person share, created only if it does not exist yet.
   *
   * One request. `POST /rooms/one-to-one` looks the room up before inserting and
   * returns the existing one, and that lookup is symmetric — any `one_to_one`
   * room you are both in, whoever opened it — so this cannot duplicate a room
   * the other person started.
   *
   * It used to read `/rooms?limit=1000&offset=0` and scan the result here:
   * every room the caller can see, fetched to answer a question about one of
   * them, and silently wrong for anyone past a thousand rooms. `GET /rooms` does
   * carry `with_user` and `type` filters for asking properly, but the create
   * already answers it, so there is nothing left to ask.
   */
  getOrCreateOneToOneRoom: async (userId: string, folderId?: string) =>
    get().createOneToOneChatRoom(userId, folderId),
  createEmailChatRoom: async (serviceProviderId: string, subject: string) => {
    try {
      const payload: Record<string, unknown> = { subject, service_provider_id: serviceProviderId };

      const response = await postRequest<any, any>(API.CHAT, `/rooms/email`, payload);

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        error: null,
      }));
      return response.data;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  updateChatRoom: async (id: string, body: CreateChatRoom) => {
    try {
      const response = await putRequest<ChatRoom, CreateChatRoom>(API.CHAT, `/rooms/${id}`, body);
      const payload = response.data?.id ? response.data : ({ ...response.data, id } as ChatRoom);

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, payload),
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
      // The caller reports it: a rename that did not save must not close as if it had.
      throw err;
    }
  },
  pinChatRoom: async (id: string, pin: boolean) => {
    try {
      const response = await postRequest<ChatRoom, any>(API.CHAT, `/rooms/${id}/pin`, { pin });
      const payload = {
        ...(response.data?.id ? response.data : { ...response.data, id }),
        pinned: pin,
      } as ChatRoom;

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, payload),
        error: null,
      }));

      await get().getChatRooms();
    } catch (e) {
      console.error(e, 'error while pinning chat room');
    }
  },
  removeChatRoom: async (id: string) => {
    try {
      await deleteRequest<void>(API.CHAT, `/rooms/${id}`);

      set((state) => ({
        chatRooms:
          state.chatRooms?.filter((room) => String(room.id) !== String(id)).map(normalizeChat) ??
          null,
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  assignChatRoom: async (id: string, usersIds: string[]) => {
    try {
      const response = await postRequest<ChatRoom, any>(API.CHAT, `/rooms/${id}/users`, {
        user_ids: usersIds,
      });

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  removeUserFromChat: async (roomId: string, userId: string) => {
    try {
      await removeRoomUser(roomId, userId);
      set((state) => ({
        chatRooms:
          state.chatRooms?.map((room) =>
            String(room.id) === String(roomId)
              ? { ...room, users: room.users?.filter((u) => String(u.id) !== String(userId)) }
              : room,
          ) ?? null,
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  addRoomToFolders: async (id: string, folderIds: string[]) => {
    try {
      await postRequest<void, { folder_ids: string[] }>(API.CHAT, `/rooms/${id}/folders`, {
        folder_ids: folderIds,
      });
      set((state) => ({
        chatRooms:
          state.chatRooms?.map((room) =>
            String(room.id) === String(id)
              ? { ...room, folder_ids: Array.from(new Set([...room.folder_ids, ...folderIds])) }
              : room,
          ) ?? null,
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  removeRoomFromFolder: async (id: string, folderId: string) => {
    try {
      await deleteRequest<void>(API.CHAT, `/rooms/${id}/folders/${folderId}`);
      set((state) => ({
        chatRooms:
          state.chatRooms?.map((room) =>
            String(room.id) === String(id)
              ? { ...room, folder_ids: room.folder_ids.filter((fid) => fid !== folderId) }
              : room,
          ) ?? null,
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  muteChat: async (id: string, isMute: { mute: boolean }) => {
    try {
      const response = await putRequest<ChatRoom, { mute: boolean }>(
        API.CHAT,
        `/rooms/${id}/mute`,
        isMute,
      );
      const payload = {
        ...(response.data?.id ? response.data : { ...response.data, id }),
        mute: isMute.mute,
      } as ChatRoom;

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, payload),
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  assignRole: async (id: string, body: AssignRolePayload) => {
    try {
      const response = await putRequest<ChatRoom, AssignRolePayload>(
        API.CHAT,
        `/rooms/${id}/assign-role`,
        body,
      );

      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, response.data),
        error: null,
      }));
    } catch (err) {
      set({ error: toError(err) });
    }
  },
  markMessagesAsUnread: async (roomId: string) => {
    try {
      await putRequest<void, undefined>(API.CHAT, `/rooms/${roomId}/messages/unread`, undefined);
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  markRoomAsUnreadLocally: (roomId: string) => {
    set((state) => ({
      chatRooms:
        state.chatRooms?.map((room) =>
          String(room.id) === String(roomId)
            ? { ...room, room_unread_messages: Math.max(room.room_unread_messages, 1) }
            : room,
        ) ?? null,
    }));
  },
  markMessagesAsRead: async (roomId: string, messageIds: string[]) => {
    try {
      await putRequest<void, { message_ids: string[] }>(
        API.CHAT,
        `/rooms/${roomId}/messages/read`,
        {
          message_ids: messageIds,
        },
      );
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  editMessage: async (roomId: string, body: UpdateMessageReq) => {
    try {
      await putRequest<void, UpdateMessageReq>(API.CHAT, `/rooms/${roomId}/messages`, body);
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  deleteMessage: async (roomId: string, messageId: string) => {
    try {
      await deleteRequest<void>(API.CHAT, `/rooms/${roomId}/messages/${messageId}`);
      set({ error: null });
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  markRoomAsReadLocally: (roomId: string) => {
    set((state) => ({
      chatRooms: state.chatRooms
        ? state.chatRooms.map((room) =>
            String(room.id) === String(roomId)
              ? { ...room, room_unread_messages: 0 }
              : normalizeChat(room),
          )
        : null,
    }));
  },
  exportChatInPdf: async (roomId: string | undefined) => {
    if (!roomId) return;

    try {
      const blob = await getRequestBlob(API.CHAT, `/rooms/${roomId}/export/pdf`);

      const date = new Date();
      const formattedDate = `${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}.${date.getFullYear()}`;

      await downloadBlob(blob, `chat-${formattedDate}.pdf`);
    } catch (error) {
      console.error('Failed to export chat to PDF:', error);
      throw error;
    }
  },
  uploadRoomAvatar: async (roomId: string, imageFile: File) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await putRequest<FileDto, FormData>(
        API.CHAT,
        `/rooms/${roomId}/image`,
        formData,
      );

      set({ error: null });

      await get().getCurrentRoomsChats(roomId);

      // The avatar is served from a stable URL, so re-uploading over an existing
      // image returns the same link — drop the cached blob to force a refetch
      // (survives navigating away/back, unlike a cache-buster that gets wiped on
      // the next room fetch).
      invalidateAuthorizedFile(response.data.link);
      return response.data;
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  removeRoomAvatar: async (roomId: string) => {
    try {
      await deleteRequest<void>(API.CHAT, `/rooms/${roomId}/image`);
      set({ error: null });
      await get().getCurrentRoomsChats(roomId);
    } catch (err) {
      set({ error: toError(err) });
      throw err;
    }
  },
  postBid: async (serviceProviderId: string, topicCode: string, folder: string | null) => {
    try {
      set({ isLoading: true, error: null });
      const response = await postRequest<
        BidRoom,
        { service_provider_id: string; topic_code: string; folder: string | null }
      >(API.CHAT, '/rooms/bid', {
        service_provider_id: serviceProviderId,
        topic_code: topicCode,
        folder,
      });
      set({ isLoading: false, error: null, bid: response.data });
      return response.data;
    } catch (error) {
      set({ isLoading: false, error: toError(error) });
      throw error;
    }
  },
  getBids: async (topicCode: string) => {
    try {
      set({ isLoading: true, error: null });
      const bids = await getBidRooms(topicCode);
      set({ isLoading: false, error: null, bids });
      return bids;
    } catch (error) {
      set({ isLoading: false, error: toError(error) });
      throw error;
    }
  },
  updateTemporaryRoomDeletionDate: async (roomId: string, galleryId: string, removeAfter: Date) => {
    await updateTemporaryRoomDeletionDate(roomId, galleryId, removeAfter);
    const room = get().chatRooms?.find((candidate) => candidate.id === roomId);
    if (room) {
      set((state) => ({
        chatRooms: upsertChat(state.chatRooms, {
          ...room,
          remove_after: removeAfter.toISOString(),
        }),
      }));
    }
  },
  addUsersToRoom: async (roomId: string, userIds: string[]) => {
    await postRequest(API.CHAT, `/rooms/${roomId}/users`, { user_ids: userIds });
  },
  removeUsersFromRoom: async (roomId: string, userId: string) => {
    await deleteRequest(API.CHAT, `/rooms/${roomId}/users`, { user_id: userId });
  },
}));
